import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { listOrgTestRuns, mapRunForList } from '../../api/controlTestRuns.js';
import { queryKeys } from '../../queryKeys.ts';
import type {
  OrgOlderAttemptApi,
  OrgOlderAttemptRow,
  OrgTestRunApiItem,
  OrgTestRunRow,
  OrgTestRunsListParams,
  OrgTestRunsResponse,
} from '../../types/controlTestRun.ts';

export interface TestRunsViewModel {
  rows: OrgTestRunRow[];
  total: number;
  totalPagesFromApi: number;
}

const mapOlderAttempt = (a: OrgOlderAttemptApi): OrgOlderAttemptRow => ({
  executionId: a.id,
  test_run_number: a.run_number,
  test_run_name: a.test_run_name ?? null,
  status: a.workflow_status,
  workflow_status: a.workflow_status,
  created_at: a.created_at ?? null,
  steps_passed: a.execution?.attributes_passed ?? 0,
  steps_failed: a.execution?.attributes_failed ?? 0,
  steps_warning: a.execution?.attributes_warning ?? 0,
  total_exceptions: a.execution?.total_exceptions ?? 0,
});

/** One API run → a table row: the control tab's row shape plus the control and list-only fields. */
export function mapOrgRunToRow(run: OrgTestRunApiItem): OrgTestRunRow {
  return {
    ...(mapRunForList(run) as Record<string, unknown>),
    soxControlId: run.sox_control_id,
    controlId: run.control?.control_id || run.control_id,
    controlTitle: run.control?.title || '',
    controlType: run.control?.control_type || '',
    preparerName: run.preparer_name ?? null,
    reviewerName: run.reviewer_name ?? null,
    attempts: run.attempts || 1,
    attemptIds: run.attempt_ids?.length ? run.attempt_ids : [run.id],
    olderAttempts: (run.older_attempts || []).map(mapOlderAttempt),
  } as OrgTestRunRow;
}

/** The org-wide Test Runs page: control-testing runs (test_runs) across every control. */
export function useTestRuns(params: OrgTestRunsListParams): UseQueryResult<TestRunsViewModel, Error> {
  return useQuery({
    queryKey: queryKeys.controlTesting.testRuns.org(params),
    queryFn: () => listOrgTestRuns(params) as Promise<OrgTestRunsResponse>,
    staleTime: 0,
    refetchOnMount: 'always',
    // Keep the current page on screen while the next one loads, so the page count never drops to 1
    // in between (which would snap the page back to 1).
    placeholderData: keepPreviousData,
    select: (data) => ({
      rows: (data.runs || []).map(mapOrgRunToRow),
      total: data.total ?? 0,
      totalPagesFromApi: data.total_pages ?? 1,
    }),
  });
}
